/**
 * Site request schemas — shared between the API (server-side request validation)
 * and the web SPA (client-side form validation).
 *
 * Spec §6 Site, §19 Site:
 *   - `name` is human-facing (display name).
 *   - `slug` is URL-safe, lowercase, kebab-case, 3–60 chars. The active form
 *     of this column is unique via a partial index on `WHERE deletedAt IS NULL`
 *     (see `prisma/migrations/0001_init/migration.sql`). We never declare
 *     `@unique` in the Prisma schema for `slug`, so all lookups go through
 *     `findFirst({ where: { slug, deletedAt: null } })`.
 *   - `themeJson` is required — a Site cannot exist without a theme — and is
 *     versioned via `themeJsonV1.schemaVersion === 1`. Older versions will be
 *     translated forward at read time by adapters living in
 *     api/src/content/adapters/ (spec §20).
 *   - `seoJson` and `settingsJson` are optional. `settingsJson` is versioned
 *     (siteSettingsJsonV1). `seoJson` is a free-form record at MVP — slice 2
 *     will pin a versioned schema for it.
 *   - `defaultPath` defaults to "/" in the database; we don't expose it on
 *     create/update yet — slice 1 owns the bare-minimum CRUD surface.
 *   - `status` is server-managed (defaults to DRAFT on create); the API does
 *     not allow setting it on create. Update is allowed via the explicit
 *     status enum to prevent string-typo regressions.
 *
 * Slug regex rationale:
 *   - lowercase letters, digits, and hyphens only,
 *   - must start and end with an alphanumeric (no leading/trailing hyphen),
 *   - no double hyphens (`--`) — these confuse human-readable URLs and are
 *     reserved for future i18n (per IDNA punycode) so disallowing them here
 *     is forward-compatible.
 *   - 3–60 characters total.
 */

import { z } from "zod";
import { themeJsonV1, siteSettingsJsonV1, brandJsonV1 } from "./json.js";

/**
 * Slug regex: `^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$`
 *   - first char alphanumeric
 *   - middle chars alphanumeric or single `-` (not followed by another `-`)
 *   - last char alphanumeric
 */
export const SITE_SLUG_REGEX = /^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$/;

export const siteSlugSchema = z
  .string()
  .min(3, "Slug must be at least 3 characters")
  .max(60, "Slug must be at most 60 characters")
  .regex(SITE_SLUG_REGEX, "Slug must be lowercase alphanumeric with single hyphens");

/**
 * SEO JSON — MVP shape is permissive while still requiring a `schemaVersion`
 * field so future adapters can migrate older shapes forward. Slice 2 will pin
 * the exact field set.
 */
export const siteSeoJsonV1 = z.object({ schemaVersion: z.literal(1) }).passthrough();
export type SiteSeoJsonV1 = z.infer<typeof siteSeoJsonV1>;

/**
 * Body schema for `POST /api/admin/sites`.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `themeJSON`
 * surfaces as a 422 instead of being silently dropped.
 */
export const createSiteSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    slug: siteSlugSchema,
    themeJson: themeJsonV1,
    seoJson: siteSeoJsonV1.optional(),
    settingsJson: siteSettingsJsonV1.optional(),
    /**
     * Optional brand brief on create (issue #193). When omitted, the service
     * persists a fresh empty envelope so the column is never null on rows
     * created post-#193. Existing rows (created before this migration) keep
     * their null until the operator saves a brief from the UI.
     */
    brandJson: brandJsonV1.optional(),
    /**
     * Optional explicit org target on create (issue #274 — multi-tenancy
     * Phase 3). When omitted, the service defaults to `resolveCurrentOrg`
     * (rule 1: the user's `currentOrganizationId`; rule 2: self-heal to the
     * oldest membership). When supplied, the service verifies the caller
     * is a member of that org — rejects with the wire-identical 404
     * SITE_NOT_FOUND otherwise (privacy-preserving: never leaks whether
     * the org exists). The field name mirrors the Prisma column so SPA
     * code does not have to translate.
     */
    organizationId: z.string().trim().min(1).optional(),
  })
  .strict();
export type CreateSiteInput = z.infer<typeof createSiteSchema>;

/**
 * Body schema for `PATCH /api/admin/sites/:id`. All fields optional; `.strict()`
 * still applies.
 */
export const updateSiteSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    slug: siteSlugSchema.optional(),
    status: z.enum(["DRAFT", "LIVE", "PAUSED", "ARCHIVED"]).optional(),
    themeJson: themeJsonV1.optional(),
    seoJson: siteSeoJsonV1.optional(),
    settingsJson: siteSettingsJsonV1.optional(),
    /**
     * Update the brand brief (issue #193). The follow-up UI surface lets
     * operators edit the four fields; this is the API path it PATCHes
     * through.
     */
    brandJson: brandJsonV1.optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>;

/**
 * Query-parameter schema for `GET /api/admin/sites`. Standard offset
 * pagination per `standards/api-patterns.md` (default 20, max 100).
 */
export const listSitesQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListSitesQuery = z.infer<typeof listSitesQuerySchema>;

/**
 * Public-facing Site shape. ISO-string timestamps for transport, JSON fields
 * untyped at the boundary (the SPA owns its own narrowing).
 */
export type SiteResponse = {
  id: string;
  name: string;
  slug: string;
  status: "DRAFT" | "LIVE" | "PAUSED" | "ARCHIVED";
  defaultPath: string;
  themeJson: unknown;
  seoJson: unknown;
  settingsJson: unknown;
  /**
   * Brand brief (issue #193). `null` for sites that existed before the
   * #193 migration AND never had a brief saved; `BrandJsonV1` otherwise.
   * The SPA narrows this through the shared schema before rendering.
   */
  brandJson: unknown;
  createdAt: string;
  updatedAt: string;
};
